import React, { FC, useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { useParams } from "react-router-dom";
import ReceiptService from "../services/ReceiptService";
import { IAPIPaymentReceiptViewModel } from "../interfaces";

/**
 * The public receipt page (/payment-receipt/:token).
 *
 * Opened from the "Download PDF" button in the payment-receipt email, so it must render without a
 * session - the token in the URL is the only credential, and RoutesConfig.restrictPublicAccess
 * whitelists this path for exactly that reason.
 *
 * "Download PDF" prints the page: a @media print block strips the page chrome and the button so the
 * browser's Save-as-PDF produces just the receipt card. That is instant and needs no infrastructure,
 * unlike a headless-render service. If the print output ever proves not good enough, only
 * `handleDownload` needs to change - swap window.print() for a call that returns a rendered file.
 *
 * The markup mirrors the design's own HTML (Web_Receipt_v2), and every value comes from the API's
 * receipt view model - the same one the email renders - so page, print and email agree by
 * construction.
 */

const PAGE_BG = "#F6F8FA";
const CARD_BORDER = "#D9DFE6";
const HAIRLINE = "#EBEFF3";
const RULE_STRONG = "#B5BEC8";
const INK_900 = "#273E52";
const INK_500 = "#5C6B7A";
const INK_300 = "#B5BEC8";
const SUCCESS = "#1F9D5C";
const SUCCESS_BG = "#E5F6ED";
const PRIMARY = "#6AB644";
const BADGE_BG = "#1F7A8C";

const Page = styled.div`
  min-height: 100vh;
  padding: 48px 0 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  box-sizing: border-box;
  background: ${PAGE_BG};
  font-family: "Open Sans", -apple-system, "Segoe UI", sans-serif;
  color: ${INK_900};

  a {
    color: ${INK_500};
    text-decoration: none;
  }
  a:hover {
    color: ${INK_900};
    text-decoration: underline;
  }

  /* Save-as-PDF: keep the card, drop the page chrome and the button. */
  @media print {
    padding: 0;
    background: #ffffff;
    min-height: auto;
    display: block;

    /* Browsers drop background colours when printing unless asked, which would blank the badge,
       the success circle and the Billed-to panel - i.e. most of the design. */
    *,
    *::before,
    *::after {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    .receipt-no-print {
      display: none !important;
    }
    .receipt-card {
      border: none !important;
      box-shadow: none !important;
      width: 100% !important;
      max-width: 100% !important;
      break-inside: avoid;
      page-break-inside: avoid;
    }
  }
`;

/** Trim the browser's default print margins so the card is not squeezed. */
const PrintPageRule = createGlobalStyle`
  @page {
    margin: 12mm;
  }
`;

const Card = styled.div`
  width: 560px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border: 1px solid ${CARD_BORDER};
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(39, 62, 82, 0.06);
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  border-bottom: 1px solid ${HAIRLINE};
`;

const Badge = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: ${BADGE_BG};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 13px;
  flex: 0 0 auto;
`;

const SuccessBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 37px 28px 24px;
`;

const CheckCircle = styled.div`
  width: 53px;
  height: 53px;
  border-radius: 999px;
  background: ${SUCCESS_BG};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
`;

const Items = styled.div`
  padding: 0 28px 24px;
  display: flex;
  flex-direction: column;
`;

// $-prefixed so styled-components treats it as a transient prop and never forwards `strong` to the
// DOM (it is not a valid div attribute).
const ItemRow = styled.div<{ $strong?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-top: 1px solid ${(p) => (p.$strong ? RULE_STRONG : HAIRLINE)};
  gap: 16px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 28px;
  padding: 20px 24px;
  background: ${PAGE_BG};
  border-radius: 12px;
  margin: 0 28px 20px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

const MetaLabel = styled.div`
  font: 600 11px "Open Sans", sans-serif;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${INK_500};
`;

/* min-width, not width: the chip has to grow for the long brands (MASTERCARD,
   AMERICAN EXPRESS) instead of letting them spill outside the border. */
const CardChip = styled.div`
  min-width: 46px;
  height: 24px;
  padding: 0 8px;
  box-sizing: border-box;
  border: 1px solid ${CARD_BORDER};
  border-radius: 4px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font: 800 italic 10px "Open Sans", sans-serif;
  color: #1a1f71;
  letter-spacing: 0.02em;
  flex: 0 0 auto;
  white-space: nowrap;

  /* Narrow phones: shrink so the longest brands still sit on one line. */
  @media (max-width: 380px) {
    font-size: 9px;
    padding: 0 6px;
  }
`;

const CardDigits = styled.span`
  font: 500 13px "JetBrains Mono", monospace;
  color: ${INK_900};
  white-space: nowrap;

  @media (max-width: 380px) {
    font-size: 12px;
  }
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${PRIMARY};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  padding: 10px 18px;
  border: none;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background: #57ac2d;
  }
`;

const Message = styled.div`
  padding: 64px 24px;
  text-align: center;
  font-size: 14px;
  color: ${INK_500};
`;

const DownloadIcon: FC = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);

/**
 * Save-as-PDF names the file after document.title, so the title IS the filename. Left alone it would
 * be the app's global "MindMe Payments", which says nothing about the document and picks up a %20
 * from the space.
 *
 * Mirrors the email template's own title ("Payment Receipt") and appends the organisation so a payer
 * filing receipts from several places can tell them apart. Hyphenated and stripped of punctuation so
 * the result is a clean filename in every browser rather than something percent-encoded.
 */
const buildReceiptDocumentTitle = (organisationName: string): string => {
  const slug = (organisationName || "")
    // Unicode-aware: \w is ASCII-only and would turn "Cafe\u0301 Mo\u0308bel" into "Caf Mbel".
    .replace(/[^\p{L}\p{N}\s-]/gu, "") // drop apostrophes, commas, slashes ...
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/-{2,}/g, "-")
    .slice(0, 60)
    .replace(/-+$/, "");
  return slug ? `Payment-Receipt-${slug}` : "Payment-Receipt";
};

interface IProps {
  /** Overrides the :token route param. Used by the confirmation screens, which already have it. */
  token?: string;
}

const PublicReceiptPage: FC<IProps> = ({ token }) => {
  const params = useParams();
  const receiptToken = token || (params as { token?: string }).token || "";

  const [receipt, setReceipt] = useState<IAPIPaymentReceiptViewModel | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    ReceiptService.getReceiptByToken(receiptToken).then((result) => {
      if (cancelled) return;
      setReceipt(result);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [receiptToken]);

  // The browser names the saved PDF after document.title, so set it from the receipt itself and put
  // the app's own title back on the way out - this page can be rendered inside the app.
  useEffect(() => {
    if (!receipt) return undefined;
    const previousTitle = document.title;
    document.title = buildReceiptDocumentTitle(receipt.subAccountName);
    return () => {
      document.title = previousTitle;
    };
  }, [receipt]);

  /**
   * Print-to-PDF. The @media print rules above reduce the page to the card, so the browser's
   * "Save as PDF" destination produces the receipt on its own. Swap this one function if a
   * server-rendered PDF is ever needed instead.
   */
  const handleDownload = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <Page>
        <Message>Loading receipt&hellip;</Message>
      </Page>
    );
  }

  if (!receipt) {
    return (
      <Page>
        <Message>
          This receipt is not available. Please check the link in your email, or
          contact the organisation that took the payment.
        </Message>
      </Page>
    );
  }

  const billedToLines = [
    receipt.billedToName,
    ...(receipt.billedToAddressLines || []),
    receipt.billedToPhone,
  ].filter((line) => line && line.trim());

  return (
    <Page>
      <PrintPageRule />
      <Card className="receipt-card">
        <CardHeader>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Badge>{receipt.subAccountInitial}</Badge>
            <div
              style={{ fontSize: "14px", fontWeight: 700, color: INK_900 }}
            >
              {receipt.subAccountName}
            </div>
          </div>
        </CardHeader>

        <SuccessBlock>
          <CheckCircle>
            <svg
              width="30"
              height="30"
              viewBox="0 0 24 24"
              fill="none"
              stroke={SUCCESS}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </CheckCircle>
          <div
            style={{
              font: '600 12px/16px "Open Sans", sans-serif',
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: INK_500,
              padding: "5px 0",
            }}
          >
            Payment successful
          </div>
          <div
            style={{
              font: '700 40px/44px "Open Sans", sans-serif',
              letterSpacing: "-0.02em",
              color: INK_900,
              fontVariantNumeric: "tabular-nums",
              paddingTop: "20px",
            }}
          >
            {receipt.amountPaid}
          </div>
          <div
            style={{ fontSize: "13px", color: INK_500, padding: "10px 0 20px" }}
          >
            {receipt.paidDateTime}
          </div>
        </SuccessBlock>

        <Items>
          {(receipt.lineItems || []).map((item, index) => (
            <ItemRow key={`${item.name}-${index}`}>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "2px" }}
              >
                <div
                  style={{ fontSize: "14px", fontWeight: 600, color: INK_900 }}
                >
                  {item.name}
                </div>
                {item.subLabel ? (
                  <div style={{ fontSize: "12px", color: INK_500 }}>
                    {item.subLabel}
                  </div>
                ) : null}
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
              >
                {item.amount}
              </div>
            </ItemRow>
          ))}

          {receipt.showProcessingFee ? (
            <ItemRow>
              <div
                style={{ fontSize: "14px", fontWeight: 600, color: INK_900 }}
              >
                Processing Fee
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
              >
                {receipt.processingFee}
              </div>
            </ItemRow>
          ) : null}

          <ItemRow $strong>
            <div style={{ fontSize: "14px", fontWeight: 600, color: INK_900 }}>
              Total Charged
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
              }}
            >
              {receipt.totalCharged}
            </div>
          </ItemRow>
        </Items>

        <MetaGrid>
          {billedToLines.length > 0 ? (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <MetaLabel>Billed to</MetaLabel>
              <div
                style={{ fontSize: "13px", lineHeight: "19px", color: INK_900 }}
              >
                {billedToLines.map((line, index) => (
                  <React.Fragment key={`${line}-${index}`}>
                    {index > 0 ? <br /> : null}
                    {line}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : (
            <div />
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <MetaLabel>Payment method</MetaLabel>
            {receipt.cardLast4 ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <CardChip>{receipt.cardBrand}</CardChip>
                <CardDigits>
                  &bull;&bull;&bull;&bull; {receipt.cardLast4}
                </CardDigits>
              </div>
            ) : (
              <div
                style={{ fontSize: "13px", lineHeight: "19px", color: INK_900 }}
              >
                {receipt.paymentMethod}
              </div>
            )}
          </div>
        </MetaGrid>

        {receipt.agencySupportEmail ? (
          <div style={{ padding: "0 28px 16px", fontSize: "12px", color: INK_500 }}>
            Questions?{" "}
            <a
              href={`mailto:${receipt.agencySupportEmail}`}
              style={{ color: INK_500, fontWeight: 600, paddingLeft: "3px" }}
            >
              {receipt.agencySupportEmail}
            </a>
          </div>
        ) : null}

        {receipt.taxFooterText ? (
          <div style={{ padding: "16px 28px 20px", borderTop: `1px solid ${HAIRLINE}` }}>
            <div style={{ fontSize: "11px", lineHeight: "16px", color: INK_500 }}>
              {receipt.taxFooterText}
            </div>
          </div>
        ) : null}
      </Card>

      <div className="receipt-no-print" style={{ display: "flex", gap: "10px" }}>
        <DownloadButton type="button" onClick={handleDownload}>
          <DownloadIcon />
          Download PDF
        </DownloadButton>
      </div>

      {receipt.showPoweredBy ? (
        <div
          className="receipt-no-print"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "11px",
            color: INK_300,
          }}
        >
          <span>
            Payments powered by <b>{receipt.poweredByAgencyName}</b>
          </span>
        </div>
      ) : null}
    </Page>
  );
};

export default PublicReceiptPage;
